import { Modal, ModalProps } from '@/modules/common/components/Modal';
import { Button } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { MouseEvent } from 'react';
import { useRemoveTeamMember } from '../../hooks/useTeams';

type RemoveTeamMemberDialog = Omit<
  ModalProps,
  'children' | 'title' | 'subTitle'
> & {
  onSuccess: () => void;
  team: string;
  user: string;
};

export function RemoveTeamMemberDialog({
  onSuccess,
  ...props
}: RemoveTeamMemberDialog) {
  const { team: teamId, user: userId } = props;

  const { mutateAsync: removeMember, isPending } = useRemoveTeamMember(
    teamId,
    userId,
  );

  const handleRemoveMember = async () => {
    try {
      await removeMember({ teamId, userId });
      onSuccess();
      enqueueSnackbar(`Member removed successfuly`, {
        variant: 'success',
      });
    } catch {
      enqueueSnackbar('An error occurred. Please try again later.', {
        variant: 'error',
      });
    }
  };

  const handleCancel = (ev: MouseEvent) => {
    props.onClose?.(ev, 'backdropClick');
  };

  return (
    <Modal
      {...props}
      title={`Remove member from team`}
      subTitle="Are you sure you want to remove this user from the team?"
      onClose={(...args) => {
        props.onClose?.(...args);
      }}
      classes={{
        paper: 'max-w-xl mx-auto p-8 pb-3',
      }}
    >
      <div>
        <p>
          By clicking 'Remove' you will permanently remove this user from the
          team. Are you sure you want to proceed?
        </p>
        <hr className="absolute left-0 right-0 mt-16" />
        <div className="mt-14 flex justify-end space-x-4">
          <Button
            variant="outlined"
            size="small"
            type="button"
            className="w-32 mt-6"
            loading={isPending}
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            type="button"
            className="w-32 mt-6"
            loading={isPending}
            onClick={handleRemoveMember}
          >
            Remove
          </Button>
        </div>
      </div>
    </Modal>
  );
}
